import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, ArrowLeft, ArrowRight, Check, Building2, Users, UserCog,
  CheckCircle2, Shield, Brain, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_OPTIONS = ['U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Senior'];
const ROLE_OPTIONS = ['Club Director', 'Technical Director', 'Head Coach', 'Administrator', 'Team Manager', 'Other'];
const ATHLETE_BUCKETS = ['<30', '30-80', '80-150', '150-300', '300+'];

const TYPE_COPY = {
  club: {
    eyebrow: 'Club Pilot Program',
    title: 'Bring your club onto Meybra',
    subtitle: 'A short onboarding to set up your club — we will prepare your workspace and reach out within 48h.',
    submitLabel: 'Request Club Pilot',
    requestType: 'club_registration',
    icon: Building2,
  },
  coach: {
    eyebrow: 'Private Coach Access',
    title: 'Start coaching with Meybra',
    subtitle: 'Tell us about your coaching practice — we will tailor your onboarding and set up your workspace.',
    submitLabel: 'Request Coach Access',
    requestType: 'coach_registration',
    icon: UserCog,
  },
  athlete: {
    eyebrow: 'Player & Family Access',
    title: 'Join Meybra as a player or family',
    subtitle: 'Tell us about your athlete — we will connect you with the right coaches and teams.',
    submitLabel: 'Request Athlete Access',
    requestType: 'athlete_registration',
    icon: Users,
  },
};

const ClubPilotPage = () => {
  const [searchParams] = useSearchParams();
  const type = useMemo(() => {
    const t = (searchParams.get('type') || 'club').toLowerCase();
    return ['club', 'coach', 'athlete'].includes(t) ? t : 'club';
  }, [searchParams]);
  const copy = TYPE_COPY[type];

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    organization_name: '',
    num_teams: '',
    approx_athlete_count: '',
    age_groups: [],
    contact_name: '',
    email: '',
    phone: '',
    role: '',
    message: '',
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAge = (age) => {
    setForm((f) => ({
      ...f,
      age_groups: f.age_groups.includes(age)
        ? f.age_groups.filter((a) => a !== age)
        : [...f.age_groups, age],
    }));
  };

  const canContinueStep1 = form.organization_name.trim().length >= 2 && (type !== 'club' || form.num_teams);
  const canContinueStep2 =
    form.contact_name.trim().length >= 2 &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    form.role;

  const submit = async () => {
    setSubmitting(true);
    try {
      if (type === 'club') {
        // Structured club onboarding (Founding Beta)
        await axios.post(`${API}/onboarding/club`, {
          club_name: form.organization_name,
          contact_name: form.contact_name,
          email: form.email,
          phone: form.phone || null,
          num_teams: form.num_teams || null,
          approx_athlete_count: form.approx_athlete_count || null,
          age_groups: form.age_groups,
          role: form.role || null,
          message: form.message || null,
          source: 'landing_pilot',
        });
      } else {
        // Coach / Athlete continue using existing contact endpoint
        await axios.post(`${API}/contact`, {
          name: form.contact_name,
          email: form.email,
          phone: form.phone,
          business_name: form.organization_name,
          sport: 'soccer',
          message:
            `[${copy.eyebrow}]\n` +
            `Organization: ${form.organization_name}\n` +
            (form.age_groups.length ? `Age Groups: ${form.age_groups.join(', ')}\n` : '') +
            `Role: ${form.role}\n\n` +
            (form.message ? `Notes: ${form.message}` : ''),
          request_type: copy.requestType,
        });
      }
      setSubmitted(true);
      setStep(3);
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  const stepTitles = [
    type === 'club' ? 'Your Club' : type === 'coach' ? 'Your Practice' : 'Your Athlete',
    'Your Contact',
    'Confirmation',
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-border/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pilot-nav-home">
            <Zap className="text-primary fill-primary" size={26} strokeWidth={2.5} />
            <span className="font-heading text-2xl tracking-wider text-white uppercase">MEYBRA</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
            data-testid="pilot-back-link"
          >
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <main className="flex-1 py-10 sm:py-16 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Eyebrow + Title */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-3 py-1 mb-5">
              <Sparkles size={12} className="text-primary" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-primary">{copy.eyebrow}</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-wide leading-tight">
              {copy.title}
            </h1>
            <p className="mt-4 text-sm text-muted-foreground max-w-xl mx-auto">{copy.subtitle}</p>
          </motion.div>

          {/* Stepper */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-10">
            {stepTitles.map((label, i) => {
              const n = i + 1;
              const active = step === n;
              const done = step > n;
              return (
                <div key={n} className="flex items-center gap-2 sm:gap-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      done
                        ? 'bg-primary text-black'
                        : active
                        ? 'bg-primary/20 border border-primary text-primary'
                        : 'bg-zinc-900 border border-border/30 text-muted-foreground'
                    }`}
                    data-testid={`pilot-step-indicator-${n}`}
                  >
                    {done ? <Check size={14} /> : n}
                  </div>
                  <span className={`hidden sm:block text-[10px] uppercase tracking-widest ${active || done ? 'text-white' : 'text-muted-foreground'}`}>
                    {label}
                  </span>
                  {n < stepTitles.length && <div className={`w-6 sm:w-10 h-px ${done ? 'bg-primary' : 'bg-border/30'}`} />}
                </div>
              );
            })}
          </div>

          {/* Panel */}
          <div className="bg-card border border-border/30 rounded-sm p-6 sm:p-10">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step-1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  data-testid="pilot-step-1"
                >
                  <p className="text-[10px] uppercase tracking-[0.25em] text-primary/70 mb-2">Step 1 of 3</p>
                  <h2 className="font-heading text-xl uppercase tracking-wide mb-6">{stepTitles[0]}</h2>

                  <div className="space-y-5">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                        {type === 'club' ? 'Club Name' : type === 'coach' ? 'Coaching Business / Your Name' : 'Athlete Full Name'} *
                      </label>
                      <Input
                        value={form.organization_name}
                        onChange={(e) => update('organization_name', e.target.value)}
                        placeholder={type === 'club' ? 'e.g. Meybra Academy' : type === 'coach' ? 'e.g. Silva Football Training' : 'e.g. Marcus Silva'}
                        className="bg-zinc-900/60 border-border/30"
                        data-testid="pilot-org-name"
                      />
                    </div>

                    {type === 'club' && (
                      <div>
                        <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                          Number of Teams *
                        </label>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                          {['1', '2-3', '4-6', '7-10', '11-15', '15+'].map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => update('num_teams', opt)}
                              className={`py-2.5 rounded-sm text-sm transition-all ${
                                form.num_teams === opt
                                  ? 'bg-primary text-black font-bold'
                                  : 'bg-zinc-900/60 border border-border/30 text-muted-foreground hover:border-primary/40'
                              }`}
                              data-testid={`pilot-teams-${opt}`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {type === 'club' && (
                      <div>
                        <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                          Approximate athlete count (optional)
                        </label>
                        <div className="grid grid-cols-5 gap-2">
                          {ATHLETE_BUCKETS.map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => update('approx_athlete_count', opt)}
                              className={`py-2 rounded-sm text-xs transition-all ${
                                form.approx_athlete_count === opt
                                  ? 'bg-primary text-black font-bold'
                                  : 'bg-zinc-900/60 border border-border/30 text-muted-foreground hover:border-primary/40'
                              }`}
                              data-testid={`pilot-athletes-${opt}`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                        Age Groups {type === 'club' ? '(select all that apply)' : '(optional)'}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {AGE_OPTIONS.map((age) => (
                          <button
                            key={age}
                            type="button"
                            onClick={() => toggleAge(age)}
                            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition-all ${
                              form.age_groups.includes(age)
                                ? 'bg-primary text-black font-bold'
                                : 'bg-zinc-900/60 border border-border/30 text-muted-foreground hover:border-primary/40'
                            }`}
                            data-testid={`pilot-age-${age}`}
                          >
                            {age}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-8">
                    <Button
                      onClick={() => setStep(2)}
                      disabled={!canContinueStep1}
                      className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider text-sm font-bold disabled:opacity-40"
                      data-testid="pilot-step-1-continue"
                    >
                      Continue <ArrowRight size={14} className="ml-2" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step-2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  data-testid="pilot-step-2"
                >
                  <p className="text-[10px] uppercase tracking-[0.25em] text-primary/70 mb-2">Step 2 of 3</p>
                  <h2 className="font-heading text-xl uppercase tracking-wide mb-6">Your Contact</h2>

                  <div className="space-y-5">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                        Full Name *
                      </label>
                      <Input
                        value={form.contact_name}
                        onChange={(e) => update('contact_name', e.target.value)}
                        placeholder="Your name"
                        className="bg-zinc-900/60 border-border/30"
                        data-testid="pilot-contact-name"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                          Email *
                        </label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e) => update('email', e.target.value)}
                          placeholder="you@example.com"
                          className="bg-zinc-900/60 border-border/30"
                          data-testid="pilot-email"
                        />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                          Phone (optional)
                        </label>
                        <Input
                          value={form.phone}
                          onChange={(e) => update('phone', e.target.value)}
                          placeholder="+1 555 000 0000"
                          className="bg-zinc-900/60 border-border/30"
                          data-testid="pilot-phone"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                        Your Role *
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {ROLE_OPTIONS.map((r) => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => update('role', r)}
                            className={`py-2.5 px-3 rounded-sm text-xs text-left transition-all ${
                              form.role === r
                                ? 'bg-primary text-black font-bold'
                                : 'bg-zinc-900/60 border border-border/30 text-muted-foreground hover:border-primary/40'
                            }`}
                            data-testid={`pilot-role-${r.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                        Anything else we should know? (optional)
                      </label>
                      <Textarea
                        value={form.message}
                        onChange={(e) => update('message', e.target.value)}
                        placeholder="Goals, specific needs, timelines..."
                        rows={3}
                        className="bg-zinc-900/60 border-border/30 resize-none"
                        data-testid="pilot-message"
                      />
                    </div>
                  </div>

                  <div className="flex justify-between mt-8">
                    <Button
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="border-border/30 text-muted-foreground uppercase tracking-wider text-sm"
                      data-testid="pilot-step-2-back"
                    >
                      <ArrowLeft size={14} className="mr-2" /> Back
                    </Button>
                    <Button
                      onClick={submit}
                      disabled={!canContinueStep2 || submitting}
                      className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider text-sm font-bold disabled:opacity-40"
                      data-testid="pilot-submit"
                    >
                      {submitting ? 'Submitting...' : copy.submitLabel}
                      {!submitting && <ArrowRight size={14} className="ml-2" />}
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 3 && submitted && (
                <motion.div
                  key="step-3"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-center py-4"
                  data-testid="pilot-step-3"
                >
                  <div className="w-16 h-16 mx-auto bg-emerald-500/15 border border-emerald-500/30 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle2 className="text-emerald-400" size={32} />
                  </div>
                  <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide text-emerald-400 mb-3">
                    Request received
                  </h2>
                  <p className="text-muted-foreground max-w-md mx-auto mb-2">
                    Thanks, <span className="text-white">{form.contact_name.split(' ')[0]}</span>. We'll reach out to{' '}
                    <span className="text-white">{form.email}</span> within 48 hours to tailor your onboarding.
                  </p>
                  <p className="text-xs text-zinc-500 mb-10">
                    We're preparing your Meybra workspace. You'll receive setup instructions from our team.
                  </p>

                  <div className="grid sm:grid-cols-3 gap-3 max-w-xl mx-auto mb-10">
                    {[
                      { icon: Shield, label: 'Secure workspace setup' },
                      { icon: Brain, label: 'AI insights configured' },
                      { icon: Sparkles, label: 'Guided onboarding call' },
                    ].map((item, i) => (
                      <div key={i} className="bg-zinc-900/40 border border-border/20 rounded-sm p-3 flex items-center gap-2 text-left">
                        <item.icon size={14} className="text-primary shrink-0" />
                        <span className="text-[11px] text-muted-foreground">{item.label}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/">
                      <Button variant="outline" className="border-border/30 text-muted-foreground uppercase tracking-wider text-sm" data-testid="pilot-back-home">
                        <ArrowLeft size={14} className="mr-2" /> Back to Home
                      </Button>
                    </Link>
                    <Link to="/unified-login">
                      <Button className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider text-sm font-bold" data-testid="pilot-go-login">
                        Go to Login <ArrowRight size={14} className="ml-2" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Trust line */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-5 text-[10px] uppercase tracking-widest text-zinc-600">
            <span className="flex items-center gap-1.5"><Shield size={11} className="text-primary/50" /> Founding Beta</span>
            <span className="flex items-center gap-1.5"><Brain size={11} className="text-primary/50" /> AI-Powered</span>
            <span className="flex items-center gap-1.5"><Check size={11} className="text-primary/50" /> No credit card required</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClubPilotPage;
